/* eslint-disable @typescript-eslint/no-floating-promises */

import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { KOALA, OLAF } from 'HubStyleTokens/colors';
import UIButton from 'UIComponents/button/UIButton';
import getIn from 'transmute/getIn';
import { getData } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import { getIsCallIncoming, getShouldShowActiveCallState } from 'calling-internal-common/widget-status/operators/getCallState';
import { getIsTabCall } from 'calling-ui-library/utils/widgetDetailsOperators';
import updateCallingReadyState from 'calling-global-api/methods/updateCallingReadyState';
import openCallingWidget from 'calling-global-api/methods/openCallingWidget';
import preloadCallingWidget from 'calling-global-api/methods/preloadCallingWidget';
import { getPropertyName, getObjectId, getObjectTypeId, getPhoneNumberProperties } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { getSource } from 'calling-lifecycle-internal/operators/from-number/FromNumberGetters';
// @ts-expect-error not typed
import SearchableCalleeList from 'calling-ui-library/callee-select/components/SearchableCalleeList';
import CallSettingsIcon from 'calling-ui-library/callee-select/components/CallSettingsIcon';

// @ts-expect-error not typed
import CurrentSettingsInfo from './CurrentSettingsInfo';
import { getCallableObjectAndAssociations } from 'calling-lifecycle-internal/callee-omnibus/operators/calleeOmnibusResultGetters';
import { READY, UNINITIALIZED, LOADING } from 'calling-internal-common/widget-status/constants/CallWidgetStates';
import { CALL_FROM_PHONE } from 'calling-internal-common/constants/CallMethods';
import { PhoneNumberSources } from 'calling-internal-common/constants/PhoneNumberSources';
import { isCallingFree } from 'calling-internal-common/utils/CallingSalesPro';
import { useWidgetDetails } from 'calling-cross-tab-library/external/useWidgetDetails';
import { getCallStatus } from 'calling-cross-tab-library/appData/operators/widgetDetailsOperators';
import { getIsProviderHubSpot, getIsTwilioBasedCallProvider } from 'calling-lifecycle-internal/call-provider/operators/getIsTwilioBasedCallProvider';
import { getInstalledCallingApplications } from '../../callee-omnibus/operators/settingsOmnibusResultGetters';
import { ChangeProviderLink } from './ChangeProviderLink';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CallNotesButtonWrapper = styled.div.withConfig({
  displayName: "CallSettingsNumbers__CallNotesButtonWrapper",
  componentId: "bdkrwe-0"
})(["position:sticky;bottom:0;background:", ";border-style:solid;border-width:2px 0 0 0;border-color:", ";"], OLAF, KOALA);
const CalleeSelectDropdownWrapper = styled.div.withConfig({
  displayName: "CallSettingsNumbers__CalleeSelectDropdownWrapper",
  componentId: "bdkrwe-1"
})(["max-height:400px;overflow:auto;"]);
const CallSettingsNumbers = props => {
  const {
    appIdentifier,
    asyncCalleeOmnibus,
    asyncSettingsOmnibus,
    widgetState,
    selectedCallProvider,
    selectedFromNumber,
    selectedCallMethod,
    scopes,
    limits,
    objectTypeId,
    subjectId,
    onSetCallStatus,
    onSetThirdPartyActive,
    onCallButtonClick,
    enableBiDirectionalProviderSwitching
  } = props;
  const {
    widgetDetails
  } = useWidgetDetails();
  const crossTabCallStatus = getCallStatus(widgetDetails);
  const handleOnOpenNotes = () => {
    openCallingWidget({
      callingProvider: selectedCallProvider
    });
    CommunicatorLogger.log('Calling_DropDownInteractions', {
      action: 'open calling widget',
      source: appIdentifier
    });
    onCallButtonClick();
  };
  const handleCallButtonClick = ({
    numberIdentifier,
    callableObject
  }) => {
    const phoneNumberProperties = getPhoneNumberProperties(callableObject);
    const numberPropertyName = getPropertyName(numberIdentifier);
    openCallingWidget({
      toSubject: {
        objectId: getObjectId(callableObject),
        objectTypeId: getObjectTypeId(callableObject),
        phoneNumberProperties,
        numberPropertyName
      },
      startCall: true,
      callingProvider: selectedCallProvider
    });
    onCallButtonClick();
    CommunicatorLogger.log('Calling_DropDownInteractions', {
      action: 'start call from dropdown',
      source: appIdentifier
    });
  };
  const handleAddPropertyClick = ({
    callableObject
  }) => {
    openCallingWidget({
      toSubject: {
        objectId: getObjectId(callableObject),
        objectTypeId: getObjectTypeId(callableObject),
        phoneNumberProperties: getPhoneNumberProperties(callableObject)
      },
      startCall: false,
      addPhoneNumber: true,
      callingProvider: selectedCallProvider
    });
    CommunicatorLogger.log('Calling_DropDownInteractions', {
      action: 'add "to" number property',
      source: appIdentifier
    });
    onCallButtonClick();
  };
  useEffect(() => {
    preloadCallingWidget();
    const callStatusFromWidgetState = getIn(['callStatus'], widgetState);
    const shouldSendUpdateReadyState = [READY, UNINITIALIZED, LOADING].includes(callStatusFromWidgetState);
    if (shouldSendUpdateReadyState) {
      updateCallingReadyState({
        subjectId,
        objectTypeId
      });
    }
  }, [objectTypeId, widgetState, subjectId]);
  const calleeOmnibusData = getData(asyncCalleeOmnibus);
  const calleesData = calleeOmnibusData && getCallableObjectAndAssociations(calleeOmnibusData);
  const shouldShowActiveCallState = getShouldShowActiveCallState(widgetState.get('callStatus'));
  const isActiveCallTab = getIsTabCall(widgetDetails);
  const isThirdPartyActive = getIn(['isThirdPartyActive'], widgetState);
  const isHubspotNumber = getSource(selectedFromNumber) === PhoneNumberSources.HUBSPOT_NUMBER;
  const isHubSpotNumberAndCallFromPhone = isHubspotNumber && selectedCallMethod === CALL_FROM_PHONE;
  const isFreeCallingAndHS = isCallingFree(scopes) && getIsProviderHubSpot(selectedCallProvider);
  const isCallTabActive = (getShouldShowActiveCallState(crossTabCallStatus) || getIsCallIncoming(crossTabCallStatus)) && isActiveCallTab;
  const disabledTooltipMessage = isHubSpotNumberAndCallFromPhone ? 'callee-phone-numbers.callFromPhoneAndHSProvidedMessage' : isFreeCallingAndHS ? 'callSettingsOptions.freeSeatHubSpotCallingBlocked' : isCallTabActive ? 'callSettingsOptions.callActiveOutboundCallBlocked' : null;
  const settingsOmnibusData = getData(asyncSettingsOmnibus);
  const installedCallingApplications = getInstalledCallingApplications(settingsOmnibusData);
  const showChangeProviderLink = useMemo(() => enableBiDirectionalProviderSwitching && installedCallingApplications && installedCallingApplications.length > 0, [installedCallingApplications, enableBiDirectionalProviderSwitching]);
  const providerName = useMemo(() => getIsTwilioBasedCallProvider(selectedCallProvider) ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "callWidgetProviders.HubSpot.widgetTitle"
  }) : selectedCallProvider.name, [selectedCallProvider]);
  const showCurrentSettingsInfo = useMemo(() => !enableBiDirectionalProviderSwitching || getIsTwilioBasedCallProvider(selectedCallProvider), [selectedCallProvider, enableBiDirectionalProviderSwitching]);
  return /*#__PURE__*/_jsxs(CalleeSelectDropdownWrapper, {
    children: [/*#__PURE__*/_jsx(SearchableCalleeList, {
      appIdentifier: appIdentifier,
      calleesData: calleesData,
      onSelectToNumber: handleCallButtonClick,
      disabled: !!disabledTooltipMessage,
      canEdit: true,
      onAddPropertyClick: handleAddPropertyClick,
      showAddPropertyButton: true,
      CallSettingsIcon: CallSettingsIcon,
      disabledTooltipMessage: disabledTooltipMessage
    }), /*#__PURE__*/_jsxs(CallNotesButtonWrapper, {
      children: [showChangeProviderLink && /*#__PURE__*/_jsx(ChangeProviderLink, {
        className: !showCurrentSettingsInfo ? 'p-bottom-2' : '',
        onCallButtonClick: onCallButtonClick,
        providerName: providerName
      }), showCurrentSettingsInfo && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(CurrentSettingsInfo, {
          appIdentifier: appIdentifier,
          asyncSettingsOmnibus: asyncSettingsOmnibus,
          selectedCallProvider: selectedCallProvider,
          selectedFromNumber: selectedFromNumber,
          selectedCallMethod: selectedCallMethod,
          scopes: scopes,
          limits: limits,
          widgetState: widgetState,
          onSetCallStatus: onSetCallStatus,
          onSetThirdPartyActive: onSetThirdPartyActive,
          disabled: isThirdPartyActive || shouldShowActiveCallState,
          enableBiDirectionalProviderSwitching: enableBiDirectionalProviderSwitching
        }), /*#__PURE__*/_jsx(UIButton, {
          use: "link",
          className: "m-left-0 p-x-4 p-y-2 width-100 option-item",
          onClick: handleOnOpenNotes,
          "data-selenium-test": "open-call-notes-button",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "callSettingsWidget.openSettingsButton"
          })
        })]
      })]
    })]
  });
};
export default CallSettingsNumbers;